import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../app/redux/useAppSelector';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { postToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';

const USE_TOGGLE_ARCHIVED_POST_FRAGMENT = gql(`
  fragment useToggleArchived_post on SlimPostFields {
    id
    archivedAt
    publishedAt
  }
`);

const ARCHIVE_POST_MUTATION = gql(/** @complexity 4 */ `
  mutation archivePost($id: ID!, $archived: Boolean!) {
    post: updatePost(id: $id, archived: $archived) {
      id
      title
      archivedAt
      archiver {
        id
        name
      }
      ...useToggleArchived_post
    }
  }
`);

const useToggleArchived = () => {
  const [archivePost] = useMutation(ARCHIVE_POST_MUTATION);
  const dispatch = useAppDispatch();

  const toggleArchived = async (
    {
      id,
      archivedAt,
      publishedAt,
    }: DocumentType<typeof USE_TOGGLE_ARCHIVED_POST_FRAGMENT>,
    {
      skipToast,
      onComplete,
    }: { skipToast?: boolean; onComplete?: () => void } = {},
  ) => {
    const isPostArchivedAfterAction = !archivedAt;
    const response = await archivePost({
      variables: { id, archived: isPostArchivedAfterAction },
    });

    onComplete?.();

    if (skipToast) {
      return null;
    }

    const isDraft = !publishedAt;
    const onUndo = () => {
      const post = response.data?.post;
      if (post) {
        toggleArchived(post, { skipToast: true, onComplete });
      }
    };

    if (isPostArchivedAfterAction) {
      dispatch(
        UIActions.pushToast(
          postToasts.postArchived({
            isDraft,
            onUndo,
          }),
        ),
      );
    } else {
      dispatch(
        UIActions.pushToast(
          postToasts.postUnarchived({
            isDraft,
            onUndo,
          }),
        ),
      );
    }

    return response;
  };

  return toggleArchived;
};

export default useToggleArchived;
